import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { noop } from "lodash";
import * as React from "react";

import { DesignToken } from "@livechat/design-system-react-components";

import { CannedResponseInputMethod } from "helpers/canneds";
import { placeCursor } from "helpers/input";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import { ICannedResponsesProps } from "../canned-responses";

import { TextareaWithCannedResponses } from "./TextareaWithCannedResponses";
import { Toolbar } from "./Toolbar";
import { insertIntoText } from "./helpers";

interface IStyledWrapperProps {
  disabled: boolean;
  privateMode: boolean;
}

const StyledWrapper = styled.div<IStyledWrapperProps>`
  border: 1px solid
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.messagebox.border.color
    };
  border-radius: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.messagebox.border.radius
  };
  position: relative;
  transition: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.input.backgroundColorTransition
  };
  cursor: text;

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    border-left: none;
    border-right: none;
    border-bottom: none;
    border-radius: 0;
    margin: 0;
  }

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.privateMode &&
      `
background: ${withVar(DesignToken.SurfaceInvertSecondary)};
color: ${withVar(DesignToken.ContentInvertPrimary)}
`
  };

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.disabled &&
      `
cursor: default;
opacity: 0.5;
`
  };
`;

const StyledTextAreaWrapper = styled.div`
  padding: 10px 12px;

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    padding: 9px 8px;
  }
`;

const StyledToolbarWrapper = styled.div`
  padding: 8px 8px 8px 12px;

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    padding: 4px;
  }
`;

interface IState {
  cannedResponsesVisible: boolean;
}

export interface IProps {
  value: string;
  placeholder: string;
  privateMode?: boolean;
  cannedResponsesByShortcuts?: ICannedResponsesProps["items"];
  maxRows?: number;
  enableAttachments?: boolean;
  disabled?: boolean;
  topNodes?: React.ReactNode;
  leftNodes?: React.ReactNode;
  rightNodes?: React.ReactNode;
  sendButtonLabel?: string;
  sendButtonDisabled?: boolean;
  sendButtonLoading?: boolean;
  onSendButtonClick(e: React.MouseEvent<HTMLButtonElement>): void;
  onInput(value: string): void;
  onChange(value: string): void;
  onTextareaRefChange?: (textarea: HTMLElement) => void;
  textareaRef?: (textarea: HTMLTextAreaElement) => void;
  onKeyDown?(e: React.KeyboardEvent<HTMLElement>): void;
  onFocus?(e: React.FocusEvent<HTMLElement>): void;
  onBlur?(e: React.FocusEvent<HTMLElement>): void;
  onResize?(): void;
  onInputMouseDown?(e: React.MouseEvent<HTMLDivElement>): void;
  onCannedResponseUse?(response: string, method: CannedResponseInputMethod): void;
  onAttachmentClick?(): void;
  onEmojiClick?(): void;
  onSendButtonMouseDown?(e: React.MouseEvent<HTMLButtonElement>): void;
}

export class MessageBox extends React.PureComponent<IProps & IWithBreakpointsProps, IState> {
  static defaultProps = {
    cannedResponsesByShortcuts: [],
    disabled: false,
    sendButtonDisabled: false,
    sendButtonLoading: false,
    privateMode: false,
    enableAttachments: false,
    textareaRef: noop,
    onKeyDown: noop,
    onFocus: noop,
    onBlur: noop,
    onResize: noop,
    onInputMouseDown: noop,
    onCannedResponseClick: noop,
    onAttachmentClick: noop,
    onEmojiClick: noop,
    onSendButtonMouseDown: noop,
  };

  state = {
    cannedResponsesVisible: false,
  };

  // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'HTMLTextAre... Remove this comment to see the full error message
  textareaRef: HTMLTextAreaElement = null;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  insertCannedResponseTrigger = () => {
    const cursorPosition = this.textareaRef.selectionEnd;
    this.props.onChange(insertIntoText(this.props.value, "#", cursorPosition));
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  triggerCannedResponsesVisibility = (shouldOpen: boolean, callback?: () => void) => {
    this.setState(
      {
        cannedResponsesVisible: shouldOpen,
      },
      () => {
        if (callback) {
          callback();
        }
      }
    );
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCannedResponseClose = (shouldPlaceCursor = true) => {
    if (shouldPlaceCursor && this.textareaRef) {
      const cursorPosition = this.textareaRef.selectionEnd;
      this.placeCursor(cursorPosition);
    }

    this.triggerCannedResponsesVisibility(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCannedResponseOpen = (isReopen = false) => {
    const callback = isReopen ? this.insertCannedResponseTrigger : null;
    // @ts-expect-error TS(2345): Argument of type '(() => void) | null' is not assi... Remove this comment to see the full error message
    this.triggerCannedResponsesVisibility(true, callback);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCannedResponsesClick = () => {
    const shouldOpen = !this.state.cannedResponsesVisible;
    const callback = shouldOpen ? this.insertCannedResponseTrigger : null;
    // @ts-expect-error TS(2345): Argument of type '(() => void) | null' is not assi... Remove this comment to see the full error message
    this.triggerCannedResponsesVisibility(shouldOpen, callback);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = e.currentTarget;
    this.props.onChange(value);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = e.currentTarget;
    this.props.onInput(value);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCannedResponseSelect = (newValue: string, response: string, method: CannedResponseInputMethod) => {
    const { onChange, onCannedResponseUse } = this.props;
    onChange(newValue);

    if (onCannedResponseUse) {
      onCannedResponseUse(response, method);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleTextareaRefChange = (textarea: HTMLTextAreaElement) => {
    this.textareaRef = textarea;
    // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    this.props.onTextareaRefChange(textarea);
  };

  placeCursor(position: number): void {
    this.textareaRef.focus();
    placeCursor(this.textareaRef, position);
  }

  render(): JSX.Element {
    const {
      isMobile,
      value,
      cannedResponsesByShortcuts,
      placeholder,
      disabled,
      sendButtonDisabled,
      sendButtonLoading,
      sendButtonLabel,
      privateMode,
      maxRows,
      enableAttachments,
      topNodes,
      leftNodes,
      rightNodes,
      textareaRef,
      onKeyDown,
      onFocus,
      onBlur,
      onResize,
      onInputMouseDown,
      onAttachmentClick,
      onSendButtonClick,
      onSendButtonMouseDown,
    } = this.props;
    const { cannedResponsesVisible } = this.state;

    return (
      <ThemeProvider theme={legacyTheme}>
        {/* @ts-expect-error TS(2322) */}
        <StyledWrapper disabled={disabled} privateMode={privateMode} onMouseDown={onInputMouseDown}>
          <StyledTextAreaWrapper>
            {topNodes}
            <TextareaWithCannedResponses
              // @ts-expect-error TS(2322): Type 'ICannedResponseTag[] | undefined' is not ass... Remove this comment to see the full error message
              cannedResponsesByShortcuts={cannedResponsesByShortcuts}
              cannedResponsesVisible={cannedResponsesVisible}
              value={value}
              placeholder={placeholder}
              // @ts-expect-error TS(2322): Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
              disabled={disabled}
              privateMode={privateMode}
              mobile={isMobile}
              maxRows={maxRows}
              innerRef={textareaRef}
              onInput={this.handleInput}
              onChange={this.handleChange}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
              onCannedResponseSelect={this.handleCannedResponseSelect}
              onCannedResponseClose={this.handleCannedResponseClose}
              onCannedResponseOpen={this.handleCannedResponseOpen}
              onTextareaRefChange={this.handleTextareaRefChange}
              onResize={onResize}
            />
          </StyledTextAreaWrapper>
          <StyledToolbarWrapper>
            {/* @ts-expect-error TS(2769): No overload matches this call. */}
            <Toolbar
              disabled={disabled}
              sendButtonDisabled={sendButtonDisabled}
              sendButtonLoading={sendButtonLoading}
              sendButtonLabel={sendButtonLabel}
              privateMode={privateMode}
              attachmentsEnabled={enableAttachments}
              mobile={isMobile}
              leftNodes={leftNodes}
              rightNodes={rightNodes}
              onCannedResponseClick={this.handleCannedResponsesClick}
              onAttachmentClick={onAttachmentClick}
              onSendButtonClick={onSendButtonClick}
              onSendButtonMouseDown={onSendButtonMouseDown}
            />
          </StyledToolbarWrapper>
        </StyledWrapper>
      </ThemeProvider>
    );
  }
}

export default withBreakpoints(MessageBox);
